/**
 * PURPOSE: Combinar edición manual de URL y carga/reemplazo de una imagen administrativa.
 * RESPONSIBILITIES: Validar el archivo en cliente, subirlo y copiar la URL resultante al formulario.
 * COLLABORATORS: hayllisAssetService.js, CardConfigEvent.jsx y ConfigForm.jsx.
 * ROUTE: winscore/src/components/Inputs/ImageUrlUploadField.jsx
 */
import React, { useRef, useState } from "react";
import { FiImage, FiUploadCloud } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { uploadHayllisAsset } from "../../services/hayllisAssetService.js";

const ACCEPTED_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_BYTES = 5 * 1024 * 1024;

const ImageUrlUploadField = ({
  label,
  value,
  onChange,
  idevent,
  idsport,
  assetType,
  disabled = false,
  placeholder = "https://...",
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const openPicker = () => {
    if (disabled || uploading) return;
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_TYPES.has(file.type)) {
      toast.error("Selecciona una imagen PNG, JPG/JPEG o WebP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("La imagen no puede superar 5 MB.");
      return;
    }

    try {
      setUploading(true);
      const result = await uploadHayllisAsset({
        file,
        idevent,
        idsport,
        assetType,
      });
      onChange(result.url);
      toast.success(
        value ? "Imagen reemplazada. Guarda la configuración." : "Imagen cargada. Guarda la configuración."
      );
    } catch (error) {
      const message =
        error?.response?.data?.message || "No se pudo subir la imagen.";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <label className="grid gap-2 text-sm font-bold text-slate-700">
      {label}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <FiImage className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="url"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            maxLength={255}
            disabled={disabled || uploading}
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 font-normal outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
          />
        </div>
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || uploading}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 font-black text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiUploadCloud />
          {uploading ? "Subiendo..." : value ? "Reemplazar imagen" : "Subir imagen"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFile}
          className="hidden"
          disabled={disabled || uploading}
        />
      </div>
      {/* <span className="text-xs font-medium text-slate-500">
        PNG, JPG/JPEG o WebP. Máximo 5 MB. También puedes pegar una URL.
      </span> */}
    </label>
  );
};

export default ImageUrlUploadField;
